import type { PatchedTripWritable } from '@bambi/api-client/schemas';
import type { UseFormGetFieldState } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { addATrip } from '../addATrip.slice';
import type { AddTripFormValues } from '../FormValues';
import { adaptFormValuesToAddTripLeg } from './adaptFormValuesToAddTripLeg';
import { adaptFormValuesToUpdate } from './adaptFormValuesToUpdate';
import { getFinalPayerPassengerExternalId } from './getFinalPayerPassengerExternalId';
import { getTripLinkToPayer } from './getTripLinkToPayer';
import { useTripRepository } from './useTripRepository';
import { validateFormInputs } from './validateFormInputs';
import { useFeature } from 'src/app/FeatureToggle/useFeature';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import type { RootState } from 'src/app/store/types';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';
import { useIsSelectedTripInThePast } from 'src/features/add-trip/useIsSelectedTripInThePast';
import { useUserRoles } from 'src/features/auth/useUserRoles';

export function useUpdateATripOnSubmit({
  getValues,
  getFieldState,
}: {
  getValues: () => any;
  getFieldState: UseFormGetFieldState<AddTripFormValues>;
}) {
  const { isRideOrderingPortalUser } = useUserRoles();
  const dispatch = useAppDispatch();
  const { updateTrip, addTripLeg } = useTripRepository();
  const { isEnabled: isTripRequiredFieldsEnabled } = useFeature(
    'trip required fields'
  );

  const selectedPayer = useAppSelector((state) => state.addATrip.selectedPayer);
  const nextToastId = useSelector((state: RootState) => state.toast.nextId);
  const isSelectedTripInThePast = useIsSelectedTripInThePast();

  return async (values: any) => {
    try {
      dispatch(addATrip.actions.submit());
      const currentValues = getValues();

      const formErrors = validateFormInputs(
        currentValues,
        selectedPayer,
        isTripRequiredFieldsEnabled
      );

      if (formErrors.length > 0) {
        dispatch(addATrip.actions.onError(formErrors));
        return;
      }

      const isAddingAdditionalLeg = currentValues.trips.length > 1;

      const linkToPayer = getTripLinkToPayer(
        !!isRideOrderingPortalUser,
        currentValues.should_link_passenger_to_payer,
        selectedPayer?.payer_type
      );
      const payerExternalId = getFinalPayerPassengerExternalId(
        linkToPayer,
        currentValues.pricing.insurance_policy_id,
        currentValues.pricing.payer_passenger_external_id
      );

      let tripResponse: any;
      if (isAddingAdditionalLeg) {
        const addTripLegPayload = adaptFormValuesToAddTripLeg(currentValues);
        tripResponse = await addTripLeg(currentValues.id, {
          original_trip: {
            ...addTripLegPayload.original_trip,
            link_to_payer: linkToPayer,
            payer_passenger_external_id: payerExternalId,
          },
          return_trip: {
            ...addTripLegPayload.return_trip,
            link_to_payer: linkToPayer,
            payer_passenger_external_id: payerExternalId,
          },
        });
      } else {
        // TODO: Need to create the adapter for past trips so we don't send fields we don't want the user to update
        const adaptedUpdateRequest: PatchedTripWritable =
          isSelectedTripInThePast
            ? adaptFormValuesToUpdate(currentValues, getFieldState)
            : adaptFormValuesToUpdate(currentValues, getFieldState);

        tripResponse = await updateTrip(currentValues.id, {
          ...adaptedUpdateRequest,
          link_to_payer: linkToPayer,
          payer_passenger_external_id: payerExternalId,
        });
      }

      if (!tripResponse) {
        if (tripResponse.error.data) {
          dispatch(addATrip.actions.onError(tripResponse.error.data.errors));
          dispatch(
            show({
              id: nextToastId,
              title: 'Error updating trip',
              description: ' Please review trip details',
              type: 'error',
            })
          );
          return;
        } else {
          dispatch(addATrip.actions.onError([]));
          dispatch(
            show({
              id: nextToastId,
              title: 'Unexpected server error',
              description: `Status: ${tripResponse.error.originalStatus}, Error: ${tripResponse.error.error} -- Please contact support`,
              type: 'error',
            })
          );
          return;
        }
      }
      dispatch(addATrip.actions.close());

      dispatch(
        show({
          id: nextToastId,
          title: 'Trip updated successfully!',
          type: 'success',
        })
      );
    } catch (e) {
      // TODO: Send error details to datadog
      const error = formatServerError(e);
      dispatch(
        addATrip.actions.onError([
          {
            attr: 'Form Error',
            code: 'general',
            detail: error,
          },
        ])
      );
    }
  };
}
